import { format, startOfDay } from 'date-fns';

import type { CalendarEvent } from './types';

// Backend stores all-day events as midnight UTC (Google sends them as bare
// "2026-05-11" date strings and we widen to a UTC timestamp). Doing
// `new Date(start_time)` west of UTC drops the local day to May 10, so any
// "which day does this event sit on?" call must read the UTC date parts as
// floating local dates for all-day events. Timed events use local startOfDay.

export function eventStartDay(e: CalendarEvent): Date | null {
  if (!e.start_time) return null;
  const d = new Date(e.start_time);
  if (e.all_day) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return startOfDay(d);
}

export function eventEndDayInclusive(e: CalendarEvent): Date | null {
  const raw = e.end_time ?? e.start_time;
  if (!raw) return null;
  const d = new Date(raw);
  if (e.all_day) {
    const end = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    // Google's all-day end is exclusive (midnight after the last covered
    // day). Step back one day so end is the last day the event covers.
    end.setDate(end.getDate() - 1);
    const start = eventStartDay(e);
    if (start && end < start) return start;
    return end;
  }
  // A timed event ending exactly at midnight (22:00–00:00) ends on the
  // *prior* day — otherwise it renders as a two-day banner / shows up on the
  // next day's list. Nudge the boundary back a millisecond before flooring.
  const start = e.start_time ? new Date(e.start_time) : null;
  if (start && d.getTime() > start.getTime() && d.getTime() === startOfDay(d).getTime()) {
    return startOfDay(new Date(d.getTime() - 1));
  }
  return startOfDay(d);
}

// --- Serialization helpers ---------------------------------------------------
// The single home for the two subtlest conventions in the app:
//   1. All-day timestamps live at UTC midnight; read/write via UTC date parts.
//   2. Google's all-day end is exclusive (midnight after the last covered
//      day); the UI always shows the inclusive last day.
// Timed values are plain local time.

// Serialize a Date for the API. All-day: the parser/grid yield local-midnight
// Dates — re-anchor at UTC midnight of the same calendar day so the backend's
// .date() records the day the user meant, not one shifted by the local offset.
export function isoForEvent(d: Date, allDay: boolean): string {
  if (!allDay) return d.toISOString();
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
}

// ISO timestamp → <input> value: 'yyyy-MM-dd' (via UTC parts) for all-day,
// local 'yyyy-MM-dd\'T\'HH:mm' for timed.
export function toLocalInput(iso: string | null, allDay: boolean): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (allDay) {
    return format(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()), 'yyyy-MM-dd');
  }
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

// <input> value → ISO timestamp. All-day inputs are bare dates anchored at
// UTC midnight; timed inputs are interpreted in local time.
export function fromLocalInput(s: string, allDay: boolean): string | null {
  const d = inputToDate(s, allDay);
  return d ? d.toISOString() : null;
}

// <input> value → Date, same conventions as fromLocalInput.
export function inputToDate(s: string, allDay: boolean): Date | null {
  if (!s) return null;
  return allDay ? new Date(`${s}T00:00:00Z`) : new Date(s);
}

// Date → <input> value using local date parts (for seeding inputs from
// grid-picked Dates, which are local-midnight for all-day).
export function dateToInput(d: Date, allDay: boolean): string {
  return allDay ? format(d, 'yyyy-MM-dd') : format(d, "yyyy-MM-dd'T'HH:mm");
}

function addDaysToDateStr(s: string, days: number): string {
  const [y, m, d] = s.split('-').map(Number);
  return format(new Date(y, m - 1, d + days), 'yyyy-MM-dd');
}

// Exclusive-end ISO → inclusive END input value (last day actually covered).
export function endToInput(endIso: string | null, allDay: boolean): string {
  const s = toLocalInput(endIso, allDay);
  return allDay && s ? addDaysToDateStr(s, -1) : s;
}

// Inclusive END input value → exclusive-end ISO.
export function endFromInput(s: string, allDay: boolean): string | null {
  return fromLocalInput(allDay && s ? addDaysToDateStr(s, 1) : s, allDay);
}

// Inclusive last-covered-day Date → Google's exclusive all-day end (+1 day),
// clamped so the end never precedes the start.
export function allDayExclusiveEnd(start: Date, endInclusive: Date): Date {
  const base = endInclusive < start ? start : endInclusive;
  return new Date(base.getTime() + 24 * 60 * 60 * 1000);
}
